<script setup lang="ts">
import { ref, watch } from 'vue';
import type { InputInst } from 'naive-ui';
import { useVaultStore } from '@/stores/vaultStore';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

// Props
const props = defineProps<{
  show: boolean;
  parentDirectoryUid?: string | null;
  parentDirectoryName?: string;
}>();

// Emits
const emit = defineEmits<{
  'update:show': [value: boolean];
  created: [];
}>();

// Store
const vaultStore = useVaultStore();

// Local state
const folderName = ref<string>('');
const isSubmitting = ref<boolean>(false);
const errorMessage = ref<string>('');
const folderNameInput = ref<InputInst | null>(null);

// Watch for modal open/close to reset form
watch(
  () => props.show,
  newValue => {
    if (!newValue) {
      // When modal is closed, reset the form
      resetForm();
    }
  }
);

// Methods
// Focus the name field once the modal has finished opening (replaces the
// `autofocus` prop, which the a11y linter disallows).
function focusFolderNameInput(): void {
  folderNameInput.value?.focus();
}

function closeModal(): void {
  emit('update:show', false);
  resetForm();
  // Clear the selection
  vaultStore.setSelectedItems([]);
}

function resetForm(): void {
  folderName.value = '';
  errorMessage.value = '';
  isSubmitting.value = false;
}

async function handleSubmit(): Promise<void> {
  // Validate
  if (!folderName.value.trim()) {
    errorMessage.value = 'Folder name is required';
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    // Use the provided parent directory UID if available
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    const parentDirUid = props.parentDirectoryUid || undefined;

    await vaultStore.createDirectory({
      name: folderName.value,
      parent_directory_uid: parentDirUid,
    });

    // Emit created event
    emit('created');

    // Close the modal and reset its state
    emit('update:show', false);
    resetForm();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to create folder';
  } finally {
    isSubmitting.value = false;
  }
}

/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--vnew-accent-color': gaiaAccent.base,
  '--vnew-mark-surface': gaiaAccent.wash,
  '--vnew-subtle-color': gaiaNeutral.textSubtle,
  '--vnew-target-surface': gaiaNeutral.surfaceTint,
};
</script>

<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="440px"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
    @after-enter="focusFolderNameInput"
  >
    <template #header>
      <n-flex align="center" :size="11" :wrap="false">
        <div class="mark">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M2 4.5h4.5l1.5 2H14v6H2z" />
          </svg>
        </div>
        <gaia-text size="subtitle" weight="semibold" tone="ink">New folder</gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="14">
      <!-- Where it lands. A read-only strip rather than a field: the target is
           decided by where you were, not something to fill in here. -->
      <n-flex vertical :size="6">
        <gaia-text size="control" weight="semibold" tone="text">Target location</gaia-text>
        <div class="target">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            :style="{ flex: 'none' }"
          >
            <path d="M2 4.5h4.5l1.5 2H14v6H2z" />
          </svg>
          <gaia-text truncate size="row" tone="tertiary">
            {{ props.parentDirectoryName || 'Home' }}
          </gaia-text>
        </div>
      </n-flex>

      <gaia-form-field label="Folder name" required :error="errorMessage || undefined">
        <gaia-input
          ref="folderNameInput"
          v-model:value="folderName"
          placeholder="Enter folder name"
          style="width: 100%"
          @keyup.enter="handleSubmit"
        />
      </gaia-form-field>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" @click="closeModal">Cancel</gaia-button>
        <gaia-create-button
          subject="folder"
          size="sm"
          :icon="false"
          :loading="isSubmitting"
          :disabled="!folderName.trim()"
          @click="handleSubmit"
        />
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
.mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--vnew-mark-surface);
  color: var(--vnew-accent-color);
}

.target {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 36px;
  padding: 0 12px;
  border-radius: 9px;
  background: var(--vnew-target-surface);
  color: var(--vnew-subtle-color);
}
</style>
